import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';

/**
 * Terms of Service — DRAFT.
 * Plain-language version. NEEDS COUNSEL REVIEW before paid tier or
 * App Store submission. Flagged inline.
 */
const Terms = () => (
  <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
    <div className="max-w-3xl mx-auto px-6">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-enf-text-muted hover:text-enf-role-info transition-colors mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to Enferno
      </Link>

      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-enf-role-info font-bold mb-3">
        <FileText className="w-3.5 h-3.5" /> Terms
      </div>
      <h1 className="font-display text-5xl md:text-6xl tracking-wide leading-none mb-3">Terms of Service</h1>
      <p className="text-enf-text-muted text-sm mb-2">Effective date: pending publication · Draft v0.1 — pending counsel review</p>
      <p className="text-enf-text-secondary text-base leading-relaxed mb-10 max-w-2xl">
        By using Enferno, you agree to these terms. Enferno is operated by
        {' '}<strong className="text-enf-text-primary">IronFire Technologies, LLC</strong>{' '}
        ("we", "us"), a Texas-formed subsidiary of Ironbridge Ventures, LLC.
      </p>

      <Section title="1. You're using a beta">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          Enferno is in private beta. Features can change without notice, bugs happen, data may move during
          migrations. We do our best to protect what you log, but you shouldn't store anything in Enferno that
          can't be recreated.
        </p>
      </Section>

      <Section title="2. Your account">
        <p className="text-enf-text-secondary text-sm leading-relaxed mb-2">
          Keep your password secure. You're responsible for activity under your account. Don't share your account
          or invite codes outside the people you trust.
        </p>
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          You can close your account anytime via Profile → Account. We may terminate accounts that violate these
          terms (abuse, fraud, harassment of other users).
        </p>
      </Section>

      <Section title="3. Acceptable use">
        <p className="text-enf-text-secondary text-sm leading-relaxed mb-2">Don't:</p>
        <ul className="list-disc pl-5 space-y-1.5 text-enf-text-secondary text-sm">
          <li>Reverse engineer, scrape, or attempt to break the service.</li>
          <li>Use Enferno to harass, threaten, or harm other users.</li>
          <li>Upload content you don't have rights to (martial arts session photos/videos in particular).</li>
          <li>Misrepresent yourself to gain access to the app or other users' data.</li>
        </ul>
      </Section>

      <Section title="4. Medical disclaimer (read this)">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          Enferno is a training journal, not a doctor. Workouts, recovery suggestions, AI coaching, pain check-ins,
          and the Activity Pacing Engine are <strong className="text-enf-text-primary">informational only</strong>. They are
          not medical advice. If you have an injury, a chronic condition, are pregnant, are recovering from
          surgery, or are returning to exercise after a long break, talk to a qualified healthcare provider before
          following any program. Stop and seek care if something hurts.
        </p>
      </Section>

      <Section title="5. AI features">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          Workouts and coach responses are generated by large language models (currently Claude Sonnet 4.5) and
          can be wrong. Use your judgment. Don't follow AI advice that contradicts what your body or your doctor
          says.
        </p>
      </Section>

      <Section title="6. Your content">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          You keep ownership of what you log. By logging it in Enferno, you grant us a license to store, process,
          and display it back to you and the users you explicitly share with. Aggregated, de-identified usage
          patterns may inform future product decisions.
        </p>
      </Section>

      <Section title="7. Subscriptions (when they exist)">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          During beta, Enferno is free. When paid tiers launch, terms specific to billing, refunds, and cancellation
          will be added here and surfaced before purchase. Auto-renewing subscriptions purchased via Apple App Store
          or Google Play are managed by those platforms.
        </p>
      </Section>

      <Section title="8. IronLimb donation pass-through">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          A portion of paid subscription revenue is donated to <strong className="text-enf-text-primary">IronLimb Foundation</strong>{' '}
          to fund adaptive equipment grants for amputee athletes. The exact pass-through percentage will be disclosed
          before paid tier launch. We are not affiliated with IronLimb beyond this mission alignment.
        </p>
      </Section>

      <Section title="9. Disclaimer of warranties">
        <p className="text-enf-text-secondary text-sm leading-relaxed uppercase tracking-wider text-xs">
          Enferno is provided "as is" and "as available" without warranties of any kind, express or implied,
          including merchantability, fitness for a particular purpose, or non-infringement.
        </p>
      </Section>

      <Section title="10. Limitation of liability">
        <p className="text-enf-text-secondary text-sm leading-relaxed uppercase tracking-wider text-xs">
          To the maximum extent permitted by law, IronFire Technologies, LLC and its affiliates are not liable for
          any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenues,
          data, or use, arising out of your use of Enferno. Total aggregate liability is limited to the greater of
          $50 or the amount you paid us in the prior 12 months.
        </p>
      </Section>

      <Section title="11. Governing law">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          These terms are governed by the laws of the State of Texas, USA, without regard to its conflict of
          law provisions. Disputes will be resolved in the state or federal courts located in Texas.
        </p>
      </Section>

      <Section title="12. Changes">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          We'll update these terms when material changes happen and notify active users by email. Continued use
          after a change means you accept the new terms.
        </p>
      </Section>

      <Section title="13. Contact">
        <p className="text-enf-text-secondary text-sm leading-relaxed">
          Questions? <a href="mailto:support@enferno.app" className="text-enf-role-info hover:text-enf-text-primary">support@enferno.app</a>
        </p>
      </Section>

      <div className="mt-10 p-4 bg-enf-role-warning/5 border border-enf-role-warning/30 rounded-md text-xs text-enf-role-warning leading-relaxed">
        <strong>DRAFT NOTICE:</strong> These terms are a plain-language placeholder pending review by counsel.
        Substantive provisions (arbitration, class action waiver, indemnification, jurisdiction details) will be
        added before paid tier launch or App Store submission.
      </div>

      <div className="mt-8 text-xs text-enf-text-muted">
        Also see: <Link to="/legal/privacy" className="text-enf-role-info hover:text-enf-text-primary">Privacy Policy</Link>
      </div>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <section className="mb-8" data-section={title.toLowerCase().replace(/\s+/g, '-')}>
    <h2 className="font-display text-2xl tracking-wide mb-3 text-enf-text-primary">{title}</h2>
    {children}
  </section>
);

export default Terms;
